import type { jsPDF } from "jspdf"

import {
  mergeSystemReportFields,
  type PreviewReportDatasourceResult,
  type ReportLayoutWidgetDto,
  type ReportTemplateDatasourceDto,
  type ReportTemplateDetailDto,
} from "modules/admin/api/reportTemplateService"
import {
  DEFAULT_FLOW_SECTION_ID,
  TABLE_PREVIEW_HEADER_HEIGHT,
  TABLE_PREVIEW_MIN_ROW_HEIGHT,
  TABLE_ROW_NO_KEY,
  WORKER_TABLE_COLUMNS,
  getCanvasMetrics,
  getLayoutFlowSections,
  getLayoutManualPageCount,
  getWidgetBorderConfig,
  getWidgetCornerRadius,
  getWidgetImageFit,
  getWidgetLineConfig,
  getWidgetPositionMode,
  getWidgetTextAlign,
  getWidgetTypographyConfig,
  getWidgetVerticalAlign,
  isLikelyImageUrl,
  normalizeLayoutForEditor,
  normalizeLayoutFrame,
  parseTableColumnWidthMap,
  resolveWidgetLabel,
  estimateFieldRenderedHeight,
  estimateTableRenderedHeight,
  type TableSourceMode,
} from "modules/admin/components/dynamic-report-editor/editorUtils"
import { buildPreviewBodyPageEntries } from "modules/admin/components/dynamic-report-editor/previewLayoutRuntime"
import {
  getDatasourceFieldDisplay,
  getDatasourceFieldLabel,
  getDatasourceTableRows,
  loadReportDatasourceResults,
} from "modules/admin/components/dynamic-report-editor/reportDatasourceRuntime"
import type { PermitDetail, PermitDetailWorker } from "modules/permit/api/permitService"

const PDF_THAI_FONT_NAME = "NotoSansThai"
const PDF_THAI_FONT_REGULAR = "NotoSansThai-Regular.ttf"
const PDF_THAI_FONT_BOLD = "NotoSansThai-Bold.ttf"
const TABLE_HEADER_HEIGHT = TABLE_PREVIEW_HEADER_HEIGHT
const TABLE_ROW_HEIGHT = TABLE_PREVIEW_MIN_ROW_HEIGHT
const TEXT_PADDING = 2

let thaiFontBinaryRegular: string | null = null
let thaiFontBinaryBold: string | null = null

type PdfDoc = jsPDF & {
  getFontList?: () => Record<string, unknown>
}

type DrawWidgetContext = {
  doc: PdfDoc
  fieldLabelMap: Map<string, string>
  getFieldDisplay: (fieldKey: string, datasourceId?: string) => string
  tableRowsByWidgetId: Map<string, string[][]>
  tableRowHeightsByWidgetId: Map<string, number[]>
  thaiFontReady: boolean
  datasources: ReportTemplateDatasourceDto[]
  datasourceResults: Record<string, PreviewReportDatasourceResult>
}

type ExportPermitLayoutPdfOptions = {
  templateDetail: ReportTemplateDetailDto
  templateCode: string
  templateName: string
  permitNumber: string
  rows: Array<Record<string, unknown>>
  workers: PermitDetailWorker[]
  fileName: string
  tenantId?: string
  permitDetail?: PermitDetail | null
}

type CreatePermitLayoutPdfResult = {
  blob: Blob
  fileName: string
  usedThaiFont: boolean
}

const resolveWorkerColumnValue = (worker: PermitDetailWorker, columnKey: string): unknown => {
  if (columnKey === "companyName") {
    return (
      worker.companyName ||
      (worker as unknown as Record<string, unknown>).company ||
      (worker as unknown as Record<string, unknown>).orgName ||
      "-"
    )
  }
  if (columnKey === "jobRole") {
    return worker.jobRole || worker.roleInPermit || "-"
  }
  return (worker as unknown as Record<string, unknown>)[columnKey]
}

const toOrientationChar = (orientation: "portrait" | "landscape") =>
  orientation === "landscape" ? "l" : "p"

const arrayBufferToBinaryString = (arrayBuffer: ArrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer)
  const chunkSize = 0x8000
  let binary = ""

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode.apply(null, Array.from(chunk))
  }
  return binary
}

const fetchFontBinary = async (fontPath: string) => {
  const response = await fetch(fontPath)
  if (!response.ok) {
    throw new Error(`Failed to load font: ${fontPath}`)
  }
  const buffer = await response.arrayBuffer()
  return arrayBufferToBinaryString(buffer)
}

const ensureThaiFontForPdf = async (doc: PdfDoc) => {
  try {
    if (!thaiFontBinaryRegular) {
      thaiFontBinaryRegular = await fetchFontBinary("/fonts/NotoSansThai-Regular.ttf")
    }
    if (!thaiFontBinaryBold) {
      thaiFontBinaryBold = await fetchFontBinary("/fonts/NotoSansThai-Bold.ttf")
    }

    const fontList = typeof doc.getFontList === "function" ? doc.getFontList() : {}
    const hasThaiFont = Boolean(fontList?.[PDF_THAI_FONT_NAME])

    if (!hasThaiFont) {
      doc.addFileToVFS(PDF_THAI_FONT_REGULAR, thaiFontBinaryRegular)
      doc.addFont(PDF_THAI_FONT_REGULAR, PDF_THAI_FONT_NAME, "normal")
      doc.addFileToVFS(PDF_THAI_FONT_BOLD, thaiFontBinaryBold)
      doc.addFont(PDF_THAI_FONT_BOLD, PDF_THAI_FONT_NAME, "bold")
    }

    doc.setFont(PDF_THAI_FONT_NAME, "normal")
    return true
  } catch (error) {
    console.warn("Thai font load failed for layout PDF export.", error)
    return false
  }
}

// Tab characters have no reliable, consistent advance width across jsPDF's own
// text-width measurement vs. how the embedded font's real glyph table renders
// them in the actual PDF viewer. That mismatch made segment widths (used to
// position the next script segment - e.g. Thai text right after a tab) drift
// out of sync with what actually got drawn, causing later text to overlap
// earlier text. Expanding tabs to plain spaces up front keeps measurement and
// rendering consistent, since space is a normal glyph every font handles.
const TAB_WIDTH_SPACES = "    "
const sanitizeTabsForPdf = (value: string) => value.replace(/\t/g, TAB_WIDTH_SPACES)

const formatPreviewValue = (value: unknown): string => {
  if (value == null) return ""
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") {
    if (Array.isArray(value)) return value.map((item) => formatPreviewValue(item)).join(", ")
    try {
      return JSON.stringify(value)
    } catch {
      return "[Object]"
    }
  }
  const normalized = String(value).trim()
  if (!normalized) return ""
  const lowered = normalized.toLowerCase()
  if (lowered === "null" || lowered === "undefined") return ""
  return sanitizeTabsForPdf(normalized)
}

const toRgb = (rawColor: unknown): [number, number, number] => {
  const color = String(rawColor || "").trim()
  const hex = color.replace(/^#/, "")
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return [
      Number.parseInt(hex.slice(0, 2), 16),
      Number.parseInt(hex.slice(2, 4), 16),
      Number.parseInt(hex.slice(4, 6), 16),
    ]
  }
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return [
      Number.parseInt(hex[0] + hex[0], 16),
      Number.parseInt(hex[1] + hex[1], 16),
      Number.parseInt(hex[2] + hex[2], 16),
    ]
  }
  return [51, 65, 85]
}

const setDashPattern = (doc: PdfDoc, borderStyle: "solid" | "dashed", borderWidth: number) => {
  if (borderStyle === "dashed") {
    doc.setLineDashPattern([Math.max(3, borderWidth * 3), Math.max(2, borderWidth * 2)], 0)
    return
  }
  doc.setLineDashPattern([], 0)
}

const drawWidgetBorder = (doc: PdfDoc, widget: ReportLayoutWidgetDto, x: number, y: number, width: number, height: number) => {
  const { borderColor, borderStyle, borderWidth, sides } = getWidgetBorderConfig(widget)
  const [r, g, b] = toRgb(borderColor)
  const radius = getWidgetCornerRadius(widget)
  const sideEnabled = [sides.top, sides.right, sides.bottom, sides.left]
  const allSides = sideEnabled.every(Boolean)
  if (borderWidth <= 0 || sideEnabled.every((value) => !value)) return

  doc.setDrawColor(r, g, b)
  doc.setLineWidth(borderWidth)
  setDashPattern(doc, borderStyle, borderWidth)

  if (allSides) {
    if (radius > 0) {
      doc.roundedRect(x, y, width, height, radius, radius, "S")
    } else {
      doc.rect(x, y, width, height, "S")
    }
    setDashPattern(doc, "solid", borderWidth)
    return
  }

  if (sides.top) doc.line(x, y, x + width, y)
  if (sides.right) doc.line(x + width, y, x + width, y + height)
  if (sides.bottom) doc.line(x, y + height, x + width, y + height)
  if (sides.left) doc.line(x, y, x, y + height)
  setDashPattern(doc, "solid", borderWidth)
}

const getFontStyle = (widget: ReportLayoutWidgetDto): "normal" | "bold" => {
  const typography = getWidgetTypographyConfig(widget)
  return typography.fontWeight === "bold" ? "bold" : "normal"
}

const THAI_TEXT_REGEX = /[\u0E00-\u0E7F]/
const SINGLE_THAI_CHAR_REGEX = /^[\u0E00-\u0E7F]$/
const NON_THAI_NON_SPACE_REGEX = /[^\u0E00-\u0E7F\s]/

const splitTextByScript = (text: string) => {
  const segments: Array<{ text: string; isThai: boolean }> = []
  let current = ""
  let currentIsThai: boolean | null = null

  for (const char of text) {
    const isThai = SINGLE_THAI_CHAR_REGEX.test(char)
    if (currentIsThai === null || currentIsThai === isThai) {
      current += char
      currentIsThai = isThai
      continue
    }

    segments.push({ text: current, isThai: currentIsThai })
    current = char
    currentIsThai = isThai
  }

  if (current) {
    segments.push({ text: current, isThai: Boolean(currentIsThai) })
  }

  return segments
}

const getSegmentWidth = (doc: PdfDoc, text: string, fontFamily: string, fontStyle: "normal" | "bold") => {
  doc.setFont(fontFamily, fontStyle)
  return doc.getTextWidth(text)
}

const shouldDrawByScriptSegments = (text: string, thaiFontReady: boolean) =>
  thaiFontReady && THAI_TEXT_REGEX.test(text) && NON_THAI_NON_SPACE_REGEX.test(text)

const resolveFittedTextLines = (
  doc: PdfDoc,
  text: string,
  maxWidth: number,
  availableHeight: number,
  preferredFontSize: number,
) => {
  const MIN_FONT_SIZE = 6
  let fontSize = Math.max(MIN_FONT_SIZE, preferredFontSize)
  let lines = doc.splitTextToSize(text, maxWidth)

  while (fontSize > MIN_FONT_SIZE) {
    const lineHeight = fontSize * 1.25
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight))
    if (lines.length <= maxLines) break
    fontSize -= 0.5
    doc.setFontSize(fontSize)
    lines = doc.splitTextToSize(text, maxWidth)
  }

  const lineHeight = fontSize * 1.25
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight))
  const clippedLines = lines.slice(0, maxLines)

  return {
    fontSize,
    lineHeight,
    clippedLines,
    textHeight: clippedLines.length * lineHeight,
  }
}

const drawTextInBox = (
  doc: PdfDoc,
  text: string,
  widget: ReportLayoutWidgetDto,
  x: number,
  y: number,
  width: number,
  height: number,
  color: [number, number, number],
  thaiFontReady: boolean,
) => {
  const safeText = text == null ? "" : String(text)
  const typography = getWidgetTypographyConfig(widget)
  const horizontal = getWidgetTextAlign(widget)
  const vertical = getWidgetVerticalAlign(widget)
  const contentPadding = Math.max(0, Math.min(TEXT_PADDING, Math.floor(Math.min(width, height) / 10)))
  const maxWidth = Math.max(8, width - contentPadding * 2)
  const availableHeight = Math.max(10, height - contentPadding * 2)
  const fitted = resolveFittedTextLines(doc, safeText, maxWidth, availableHeight, typography.fontSize)
  const { clippedLines, textHeight, lineHeight, fontSize } = fitted
  const baseTop =
    vertical === "center"
      ? y + (height - textHeight) / 2
      : vertical === "bottom"
        ? y + height - contentPadding - textHeight
        : y + contentPadding

  const xPos =
    horizontal === "center"
      ? x + width / 2
      : horizontal === "right"
        ? x + width - contentPadding
        : x + contentPadding
  const align = horizontal === "center" ? "center" : horizontal === "right" ? "right" : "left"

  doc.setTextColor(color[0], color[1], color[2])
  doc.setFontSize(fontSize)
  const fontStyle = getFontStyle(widget)

  clippedLines.forEach((line: string, index: number) => {
    const yPos = baseTop + typography.fontSize + index * lineHeight
    const lineText = String(line)
    // Pick the font per line, not once for the whole (possibly multi-line)
    // value: a value can have Thai text on one line and pure Latin text on
    // another (e.g. wrapped after an explicit newline). Basing this on the
    // whole value forced Latin-only lines onto the Thai font, which lacks
    // full Latin glyph coverage and rendered as missing/blank characters.
    const lineFontFamily =
      thaiFontReady && THAI_TEXT_REGEX.test(lineText) ? PDF_THAI_FONT_NAME : "helvetica"

    if (!shouldDrawByScriptSegments(lineText, thaiFontReady)) {
      doc.setFont(lineFontFamily, fontStyle)
      doc.text(lineText, xPos, yPos, { align })
      return
    }

    const segments = splitTextByScript(lineText)
    if (segments.length <= 1) {
      doc.setFont(lineFontFamily, fontStyle)
      doc.text(lineText, xPos, yPos, { align })
      return
    }

    const segmentWidths = segments.map((segment) =>
      getSegmentWidth(doc, segment.text, segment.isThai ? PDF_THAI_FONT_NAME : "helvetica", fontStyle),
    )
    const totalWidth = segmentWidths.reduce((sum, widthValue) => sum + widthValue, 0)
    const startX =
      align === "center" ? xPos - totalWidth / 2 : align === "right" ? xPos - totalWidth : xPos

    let cursorX = startX
    segments.forEach((segment, segmentIndex) => {
      const segmentFont = segment.isThai ? PDF_THAI_FONT_NAME : "helvetica"
      doc.setFont(segmentFont, fontStyle)
      doc.text(segment.text, cursorX, yPos, { align: "left" })
      cursorX += segmentWidths[segmentIndex]
    })
  })
}

const dataUrlCache = new Map<string, { dataUrl: string; width: number; height: number }>()

const toDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(new Error("Failed to read image blob"))
    reader.readAsDataURL(blob)
  })

const getImageSize = (dataUrl: string) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height })
    image.onerror = () => reject(new Error("Failed to load image data"))
    image.src = dataUrl
  })

const loadImageData = async (imageUrl: string) => {
  const key = imageUrl.trim()
  if (!key) return null
  const cached = dataUrlCache.get(key)
  if (cached) return cached

  let dataUrl = key
  if (!key.startsWith("data:image/")) {
    const response = await fetch(key)
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`)
    }
    const blob = await response.blob()
    dataUrl = await toDataUrl(blob)
  }

  const size = await getImageSize(dataUrl)
  const loaded = { dataUrl, width: size.width, height: size.height }
  dataUrlCache.set(key, loaded)
  return loaded
}

const drawImageWidget = async (
  doc: PdfDoc,
  widget: ReportLayoutWidgetDto,
  x: number,
  y: number,
  width: number,
  height: number,
  getFieldDisplay: (fieldKey: string, datasourceId?: string) => string,
  thaiFontReady: boolean,
) => {
  const bindingType = String(widget.props?.bindingType || "static")
  const staticUrl = String(widget.props?.staticUrl || "")
  const fieldKey = String(widget.props?.fieldKey || "")
  const imageFit = getWidgetImageFit(widget)
  const boundValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
  const candidateUrl =
    bindingType === "field"
      ? (isLikelyImageUrl(boundValue) ? boundValue : "")
      : (isLikelyImageUrl(staticUrl) ? staticUrl : "")

  if (!candidateUrl) {
    drawTextInBox(doc, "Image placeholder", widget, x, y, width, height, [107, 114, 128], thaiFontReady)
    return
  }

  try {
    const loaded = await loadImageData(candidateUrl)
    if (!loaded) {
      drawTextInBox(doc, "Image placeholder", widget, x, y, width, height, [107, 114, 128], thaiFontReady)
      return
    }

    if (imageFit === "fill") {
      doc.addImage(loaded.dataUrl, "PNG", x, y, width, height, undefined, "FAST")
      return
    }

    const ratio = loaded.width / loaded.height
    const boxRatio = width / height
    let drawWidth = width
    let drawHeight = height
    if (imageFit === "cover") {
      if (ratio > boxRatio) {
        drawHeight = height
        drawWidth = height * ratio
      } else {
        drawWidth = width
        drawHeight = width / ratio
      }
    } else {
      if (ratio > boxRatio) {
        drawWidth = width
        drawHeight = width / ratio
      } else {
        drawHeight = height
        drawWidth = height * ratio
      }
    }
    const drawX = x + (width - drawWidth) / 2
    const drawY = y + (height - drawHeight) / 2
    doc.addImage(loaded.dataUrl, "PNG", drawX, drawY, drawWidth, drawHeight, undefined, "FAST")
  } catch (error) {
    console.warn("Image render failed in layout PDF export.", error)
    drawTextInBox(doc, "Image unavailable", widget, x, y, width, height, [107, 114, 128], thaiFontReady)
  }
}

const resolveTableColumnPercents = (widget: ReportLayoutWidgetDto, columns: string[]) => {
  if (columns.length === 0) return []
  const map = parseTableColumnWidthMap(widget.props?.columnWidths)
  const fixedColumns = columns.filter((columnKey) => Number.isFinite(map[columnKey]) && map[columnKey] > 0)
  const fixedTotal = fixedColumns.reduce((sum, columnKey) => sum + map[columnKey], 0)
  const dynamicColumns = columns.filter((columnKey) => !fixedColumns.includes(columnKey))
  const remaining = Math.max(0, 100 - fixedTotal)

  const base: number[] = columns.map((columnKey) => {
    const fixed = map[columnKey]
    if (Number.isFinite(fixed) && fixed > 0) return fixed
    if (dynamicColumns.length === 0) return 0
    return remaining / dynamicColumns.length
  })

  const total = base.reduce((sum, value) => sum + value, 0)
  if (total <= 0) return columns.map(() => 100 / columns.length)
  return base.map((value) => (value / total) * 100)
}

const drawTableWidget = (
  context: DrawWidgetContext,
  widget: ReportLayoutWidgetDto,
  x: number,
  y: number,
  width: number,
  defaultHeight: number,
) => {
  const { doc, tableRowsByWidgetId, tableRowHeightsByWidgetId, fieldLabelMap } = context
  const columns = String(widget.props?.columns || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)

  const tableRows = tableRowsByWidgetId.get(widget.id) ?? []
  const safeRows = tableRows.length > 0 ? tableRows : (columns.length > 0 ? [columns.map(() => "")] : [[""]])
  const isWorkerTable = String(widget.props?.tableSource || "reportRows") === "workers"
  const rowHeights = isWorkerTable
    ? (tableRowHeightsByWidgetId.get(widget.id) ?? safeRows.map(() => TABLE_ROW_HEIGHT))
    : safeRows.map(() => TABLE_ROW_HEIGHT)
  const rowHeight = TABLE_ROW_HEIGHT
  const headerHeight = TABLE_HEADER_HEIGHT
  const isFlowTable = getWidgetPositionMode(widget) === "flow"
  const renderedHeight = estimateTableRenderedHeight(widget, columns, safeRows)
  const tableHeight = isFlowTable ? renderedHeight : Math.max(defaultHeight, renderedHeight)
  const columnPercents = resolveTableColumnPercents(widget, columns.length > 0 ? columns : ["__single"])
  const labels =
    columns.length > 0
      ? columns.map((columnKey) => {
        if (columnKey === TABLE_ROW_NO_KEY) return "No."
        if (String(widget.props?.tableSource || "reportRows") === "datasource") {
          return getDatasourceFieldLabel(
            context.datasources,
            context.datasourceResults,
            String(widget.props?.datasourceId || ""),
            columnKey,
          )
        }
        const workerLabel = WORKER_TABLE_COLUMNS.find((item) => item.key === columnKey)?.label
        return workerLabel || fieldLabelMap.get(columnKey) || columnKey
      })
      : ["No columns"]

  const toColumnWidth = (index: number) => {
    const percent = columnPercents[index] ?? (100 / Math.max(1, labels.length))
    return Math.max(20, (width * percent) / 100)
  }

  let columnX = x
  labels.forEach((label, index) => {
    const columnWidth = toColumnWidth(index)
    doc.setFillColor(249, 250, 251)
    doc.rect(columnX, y, columnWidth, headerHeight, "FD")
    drawTextInBox(doc, label, widget, columnX, y, columnWidth, headerHeight, [55, 65, 81], context.thaiFontReady)
    columnX += columnWidth
  })

  let currentRowY = y + headerHeight
  safeRows.forEach((rowValues, rowIndex) => {
    const currentRowHeight = rowHeights[rowIndex] ?? rowHeight
    const rowY = currentRowY
    let rowX = x
    labels.forEach((_, columnIndex) => {
      const columnWidth = toColumnWidth(columnIndex)
      doc.setFillColor(255, 255, 255)
      doc.rect(rowX, rowY, columnWidth, currentRowHeight, "FD")
      drawTextInBox(doc, rowValues[columnIndex] ?? "", widget, rowX, rowY, columnWidth, currentRowHeight, [31, 41, 55], context.thaiFontReady)
      rowX += columnWidth
    })
    currentRowY += currentRowHeight
  })

  drawWidgetBorder(doc, widget, x, y, width, tableHeight)
}

const drawFrame = (
  doc: PdfDoc,
  layout: ReportTemplateDetailDto["layoutJson"],
  canvasWidth: number,
  canvasHeight: number,
  headerHeight: number,
  footerHeight: number,
) => {
  const frame = normalizeLayoutFrame(layout?.frame)
  if (!frame.enabled) return

  const topOffset = frame.scope === "body" ? headerHeight : 0
  const heightScope = frame.scope === "body"
    ? Math.max(40, canvasHeight - headerHeight - footerHeight)
    : canvasHeight

  const x = frame.insetLeft
  const y = topOffset + frame.insetTop
  const width = Math.max(20, canvasWidth - frame.insetLeft - frame.insetRight)
  const height = Math.max(20, heightScope - frame.insetTop - frame.insetBottom)
  const [r, g, b] = toRgb(frame.borderColor)

  doc.setDrawColor(r, g, b)
  doc.setLineWidth(Math.max(1, frame.borderWidth))
  setDashPattern(doc, frame.borderStyle, frame.borderWidth)
  if ((frame.cornerRadius ?? 0) > 0) {
    doc.roundedRect(x, y, width, height, frame.cornerRadius ?? 0, frame.cornerRadius ?? 0, "S")
  } else {
    doc.rect(x, y, width, height, "S")
  }
  setDashPattern(doc, "solid", frame.borderWidth)
}

const drawWidget = async (
  context: DrawWidgetContext,
  widget: ReportLayoutWidgetDto,
  renderY: number,
  renderHeightOverride?: number,
) => {
  const { doc, getFieldDisplay } = context
  const x = Math.max(0, Number(widget.x || 0))
  const y = Math.max(0, Number(renderY || 0))
  const width = Math.max(1, Number(widget.width || 1))
  const height = Math.max(1, Number.isFinite(renderHeightOverride) ? Number(renderHeightOverride) : Number(widget.height || 1))
  const [defaultR, defaultG, defaultB] = [31, 41, 55]

  if (widget.type === "rectangle") {
    const fillColor = String(widget.props?.fillColor || "transparent")
    if (fillColor && fillColor !== "transparent") {
      const [r, g, b] = toRgb(fillColor)
      doc.setFillColor(r, g, b)
      const radius = getWidgetCornerRadius(widget)
      if (radius > 0) {
        doc.roundedRect(x, y, width, height, radius, radius, "F")
      } else {
        doc.rect(x, y, width, height, "F")
      }
    }
    drawWidgetBorder(doc, widget, x, y, width, height)
    return
  }

  if (widget.type === "line") {
    drawWidgetBorder(doc, widget, x, y, width, height)
    const line = getWidgetLineConfig(widget)
    const [r, g, b] = toRgb(line.lineColor)
    const centerX = x + width / 2
    const centerY = y + height / 2
    const radians = (line.lineAngle * Math.PI) / 180
    const halfLength = width / 2
    const x1 = centerX - Math.cos(radians) * halfLength
    const y1 = centerY - Math.sin(radians) * halfLength
    const x2 = centerX + Math.cos(radians) * halfLength
    const y2 = centerY + Math.sin(radians) * halfLength
    doc.setDrawColor(r, g, b)
    doc.setLineWidth(Math.max(1, line.lineThickness))
    setDashPattern(doc, line.lineStyle, line.lineThickness)
    doc.line(x1, y1, x2, y2)
    setDashPattern(doc, "solid", line.lineThickness)
    return
  }

  if (widget.type === "image") {
    drawWidgetBorder(doc, widget, x, y, width, height)
    await drawImageWidget(doc, widget, x, y, width, height, getFieldDisplay, context.thaiFontReady)
    return
  }

  if (widget.type === "table") {
    drawTableWidget(context, widget, x, y, width, height)
    return
  }

  if (widget.type === "field") {
    const fieldKey = String(widget.props?.fieldKey || "")
    const fieldLabel = resolveWidgetLabel(widget, context.fieldLabelMap.get(fieldKey) || "Field")
    const fieldValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
    const hasFieldLabel = fieldLabel.trim().length > 0
    const typography = getWidgetTypographyConfig(widget)
    const lineHeight = typography.fontSize * 1.25
    const effectiveHeight =
      getWidgetPositionMode(widget) === "flow"
        ? Math.max(height, estimateFieldRenderedHeight(widget, fieldLabel, fieldValue))
        : height
    const labelHeight = hasFieldLabel
      ? Math.max(0, estimateFieldRenderedHeight(widget, fieldLabel, "") - TEXT_PADDING * 2 - (lineHeight + 2))
      : 0
    drawWidgetBorder(doc, widget, x, y, width, effectiveHeight)
    if (hasFieldLabel) {
      drawTextInBox(doc, fieldLabel, widget, x, y, width, labelHeight, [107, 114, 128], context.thaiFontReady)
    }
    drawTextInBox(doc, fieldValue, widget, x, y + labelHeight, width, Math.max(8, effectiveHeight - labelHeight), [defaultR, defaultG, defaultB], context.thaiFontReady)
    return
  }

  drawWidgetBorder(doc, widget, x, y, width, height)

  drawTextInBox(doc, formatPreviewValue(widget.props?.text), widget, x, y, width, height, [defaultR, defaultG, defaultB], context.thaiFontReady)
}

export async function createPermitLayoutPdfBlob({
  templateDetail,
  templateCode,
  templateName,
  permitNumber,
  rows,
  workers,
  fileName,
  tenantId = "",
  permitDetail = null,
}: ExportPermitLayoutPdfOptions): Promise<CreatePermitLayoutPdfResult> {
  if (!templateDetail.layoutJson) {
    throw new Error("Report template has no layout")
  }

  const layout = normalizeLayoutForEditor(templateDetail.layoutJson)
  const fullCanvasMetrics = getCanvasMetrics(layout.orientation)
  const headerEnabled = Boolean(layout.headerSection?.enabled)
  const footerEnabled = Boolean(layout.footerSection?.enabled)
  const headerHeight = headerEnabled ? Math.max(60, Number(layout.headerSection?.height || 120)) : 0
  const footerHeight = footerEnabled ? Math.max(60, Number(layout.footerSection?.height || 80)) : 0
  const baseBodyCanvasHeight = Math.max(200, fullCanvasMetrics.height - headerHeight - footerHeight)
  const flowSections = getLayoutFlowSections(layout)
  const manualPageCount = getLayoutManualPageCount(layout)
  const primaryFlowSectionId = flowSections[0]?.sectionId || DEFAULT_FLOW_SECTION_ID
  const reportDatasources = layout.datasources ?? []

  const fieldLabelMap = new Map(mergeSystemReportFields(templateDetail.fields ?? []).map((field) => [field.fieldKey, field.label || field.fieldKey]))
  const firstRow = rows[0] ?? {}
  const previewValueMap = new Map(
    Object.entries(firstRow).map(([key, value]) => [String(key), formatPreviewValue(value)]),
  )
  const datasourceResults =
    tenantId && reportDatasources.length > 0
      ? await loadReportDatasourceResults({
          tenantId,
          datasources: reportDatasources,
          row: firstRow,
          permitDetail,
        })
      : {}
  const getFieldDisplay = (fieldKey: string, datasourceId?: string) => {
    if (datasourceId) {
      return getDatasourceFieldDisplay(datasourceResults, datasourceId, fieldKey)
    }
    const value = previewValueMap.get(fieldKey)
    if (value && value.trim()) return value
    if (fieldKey === "permit_number" && permitNumber) return formatPreviewValue(permitNumber)
    return ""
  }

  const getTablePreviewRows = (widget: ReportLayoutWidgetDto, columns: string[]) => {
    if (columns.length === 0) return []
    const source: TableSourceMode =
      String(widget.props?.tableSource || "reportRows") === "workers"
        ? "workers"
        : String(widget.props?.tableSource || "reportRows") === "datasource"
          ? "datasource"
          : "reportRows"
    if (source === "workers") {
      return workers.map((worker, rowIndex) =>
        columns.map((columnKey) => {
          if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
          const value = resolveWorkerColumnValue(worker, columnKey)
          return formatPreviewValue(value)
        }),
      )
    }

    if (source === "datasource") {
      return getDatasourceTableRows(
        datasourceResults,
        String(widget.props?.datasourceId || ""),
        columns.filter((columnKey) => columnKey !== TABLE_ROW_NO_KEY),
      ).map((row, rowIndex) =>
        columns.map((columnKey, columnIndex) => {
          if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
          const safeIndex = columns.slice(0, columnIndex).filter((item) => item !== TABLE_ROW_NO_KEY).length
          return row[safeIndex] ?? ""
        }),
      )
    }

    if (!Array.isArray(rows) || rows.length === 0) return []
    return rows.map((row, rowIndex) =>
      columns.map((columnKey) => {
        if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
        return formatPreviewValue(row[columnKey])
      }),
    )
  }

  const previewBodyWidgets = [...(layout.widgets ?? [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
  const previewBodyPageEntries = buildPreviewBodyPageEntries({
    baseBodyCanvasHeight,
    flowSections,
    manualPageCount,
    previewBodyWidgets,
    primaryFlowSectionId,
    getTablePreviewRows,
    getWidgetMeasuredHeight: (widget) => {
      if (widget.type !== "field") return undefined
      const fieldKey = String(widget.props?.fieldKey || "")
      const fieldLabel = resolveWidgetLabel(widget, fieldLabelMap.get(fieldKey) || "Field")
      const fieldValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
      return estimateFieldRenderedHeight(widget, fieldLabel, fieldValue)
    },
  })
  const pageIndexes = Array.from(previewBodyPageEntries.keys())
  const totalPages = pageIndexes.length > 0
    ? Math.max(1, manualPageCount, Math.max(...pageIndexes) + 1)
    : Math.max(1, manualPageCount)

  const [{ jsPDF }] = await Promise.all([
    import("jspdf"),
  ])

  const doc = new jsPDF({
    unit: "pt",
    format: [fullCanvasMetrics.width, fullCanvasMetrics.height],
    orientation: toOrientationChar(layout.orientation),
  }) as PdfDoc

  const usedThaiFont = await ensureThaiFontForPdf(doc)
  const fontFamily = usedThaiFont ? PDF_THAI_FONT_NAME : "helvetica"
  doc.setFont(fontFamily, "normal")
  doc.setProperties({
    title: `${templateName || templateCode} - ${permitNumber}`,
    subject: "Permit Dynamic Report",
    creator: "PermitFy",
  })

  const headerWidgets = [...(layout.headerSection?.widgets ?? [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
  const footerWidgets = [...(layout.footerSection?.widgets ?? [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
  const bodyOffsetY = headerEnabled ? headerHeight : 0
  const footerOffsetY = fullCanvasMetrics.height - footerHeight

  for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
    if (pageIndex > 0) {
      doc.addPage([fullCanvasMetrics.width, fullCanvasMetrics.height], toOrientationChar(layout.orientation))
      doc.setFont(fontFamily, "normal")
    }

    drawFrame(doc, layout, fullCanvasMetrics.width, fullCanvasMetrics.height, headerHeight, footerHeight)

    const tableRowsByWidgetId = new Map<string, string[][]>()
    const tableRowHeightsByWidgetId = new Map<string, number[]>()
    const pageBodyEntries = [...(previewBodyPageEntries.get(pageIndex) ?? [])]
      .sort((a, b) => {
        const yDiff = a.renderY - b.renderY
        if (yDiff !== 0) return yDiff
        return (a.widget.zIndex || 0) - (b.widget.zIndex || 0)
      })
    pageBodyEntries.forEach((entry) => {
      tableRowsByWidgetId.set(entry.widget.id, entry.tableRows ?? [])
      tableRowHeightsByWidgetId.set(entry.widget.id, entry.tableRowHeights ?? [])
    })

    const context: DrawWidgetContext = {
      doc,
      fieldLabelMap,
      getFieldDisplay,
      tableRowsByWidgetId,
      tableRowHeightsByWidgetId,
      thaiFontReady: usedThaiFont,
      datasources: reportDatasources,
      datasourceResults,
    }

    if (headerEnabled) {
      for (const widget of headerWidgets) {
        await drawWidget(context, widget, Number(widget.y || 0))
      }
    }

    for (const entry of pageBodyEntries) {
      await drawWidget(context, entry.widget, bodyOffsetY + entry.renderY, entry.renderHeight)
    }

    if (footerEnabled) {
      for (const widget of footerWidgets) {
        await drawWidget(context, widget, footerOffsetY + Number(widget.y || 0))
      }
    }
  }

  return {
    blob: doc.output("blob"),
    fileName,
    usedThaiFont,
  }
}

export async function exportPermitLayoutPdf(options: ExportPermitLayoutPdfOptions): Promise<boolean> {
  const { blob, fileName, usedThaiFont } = await createPermitLayoutPdfBlob(options)
  const blobUrl = URL.createObjectURL(blob)
  const downloadLink = document.createElement("a")
  downloadLink.href = blobUrl
  downloadLink.download = fileName
  document.body.appendChild(downloadLink)
  downloadLink.click()
  downloadLink.remove()
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)

  return usedThaiFont
}
